import React, { Fragment, useEffect, useMemo, useState } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";

import {
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";

interface TableContainerProps {
  columns?: any;
  data?: any;
  isGlobalFilter?: any;
  handleTaskClick?: any;
  customPageSize?: any;
  tableClass?: any;
  theadClass?: any;
  trClass?: any;
  thClass?: any;
  divClass?: any;
  rowClassName?: (row: any) => string;
  rowColSpan?: (row: any) => boolean;
  rowCellRenderer?: (row: any) => React.ReactNode;
  SearchPlaceholder?: any;
  handleLeadClick?: any;
  handleCompanyClick?: any;
  handleContactClick?: any;
  handleTicketClick?: any;
  isBordered?: any;
  globalFilterValue?: string;
  onGlobalFilterChange?: (value: string) => void;
  emptyMessage?: string;
}

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control border-0 search"
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  rowClassName,
  rowColSpan,
  rowCellRenderer,
  SearchPlaceholder,
  globalFilterValue,
  onGlobalFilterChange,
  emptyMessage,
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [internalGlobalFilter, setInternalGlobalFilter] = useState("");
  const globalFilter = useMemo(
    () => (typeof globalFilterValue === "string" ? globalFilterValue : internalGlobalFilter),
    [globalFilterValue, internalGlobalFilter],
  );
  const setGlobalFilterValue = onGlobalFilterChange ?? setInternalGlobalFilter;

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilterValue,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    setPageSize,
  } = table;

  useEffect(() => {
    Number(customPageSize) && setPageSize(Number(customPageSize));
  }, [customPageSize, setPageSize]);

  return (
    <Fragment>
      {isGlobalFilter && (
        <Row className="mb-3">
          <CardBody className="border border-dashed border-end-0 border-start-0">
            <form>
              <Row>
                <Col sm={5}>
                  <div className="search-box me-2 mb-2 d-inline-block col-12">
                    <DebouncedInput
                      value={globalFilter ?? ""}
                      onChange={(value) => setGlobalFilterValue(String(value))}
                      placeholder={SearchPlaceholder || "Search..."}
                    />
                    <i className="bx bx-search-alt search-icon"></i>
                  </div>
                </Col>
              </Row>
            </form>
          </CardBody>
        </Row>
      )}

      <div className={divClass}>
        <Table hover className={tableClass}>
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => {
                  const canSort = header.column.getCanSort?.() ?? false;
                  const sortedState = header.column.getIsSorted();
                  let sortIconClass = "ri-arrow-up-down-line text-muted";
                  if (sortedState === "asc") {
                    sortIconClass = "ri-arrow-up-line text-primary";
                  } else if (sortedState === "desc") {
                    sortIconClass = "ri-arrow-down-line text-primary";
                  }
                  return (
                    <th key={header.id} className={thClass}>
                      {header.isPlaceholder ? null : (
                        <div className="d-flex align-items-center gap-2">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {canSort ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-muted"
                              onClick={(event) => {
                                event.stopPropagation();
                                const handler =
                                  header.column.getToggleSortingHandler();
                                handler?.(event);
                              }}
                            >
                              <i className={sortIconClass}></i>
                            </button>
                          ) : null}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns?.length ?? 1}
                  className="table-empty-state-cell text-center py-4 text-muted"
                >
                  {emptyMessage ?? "Seleccioná servidor, sucursal y periodo para ver resultados."}
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row: any) => {
                const isTotalRow = row.original?.__isTotal;
                const isCollectionSubtotal = row.original?.__isCollectionSubtotal;
                const isGrandTotal = row.original?.__isGrandTotal;
                let totalClasses = "";
                if (isTotalRow) {
                  totalClasses = isCollectionSubtotal
                    ? "table-info-subtle border-top border-info-subtle"
                    : "table-warning-subtle border-top border-warning-subtle";
                  if (isGrandTotal) {
                    totalClasses = "table-warning-subtle border-top border-warning-subtle";
                  }
                }
                const customRowClass = rowClassName ? rowClassName(row) : "";
                const useColSpan = rowColSpan ? rowColSpan(row) : false;
                return (
                  <tr
                    key={row.id}
                    className={[trClass, totalClasses, customRowClass]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {useColSpan ? (
                      <td colSpan={row.getVisibleCells().length}>
                        {rowCellRenderer ? rowCellRenderer(row) : null}
                      </td>
                    ) : (
                      row.getVisibleCells().map((cell: any) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </Fragment>
  );
};

export default TableContainer;
